"use client";

import React from "react";
import { useRef } from "react";
import { useState } from "react";
import { Formik, Form, Field, FieldArray } from "formik";
import { useEffect } from "react";
import * as Yup from "yup";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  Select,
  TextField,
  Typography,
  Link as MuiLink,
  RadioGroup,
} from "@mui/material";
import {
  FaUserGraduate,
  FaHome,
  FaMoneyBillWave,
  FaFileAlt,
  FaUsers,
  FaUserTie,
  FaPlus,
  FaTrash,
  FaUpload,
  FaInfoCircle,
  FaSave,
  FaGraduationCap,
  FaCheckCircle,
} from "react-icons/fa";
import api from "../../api/axiosInstance";
import type { ScholarshipApplicationForm } from "../../typings";
import {
  SCHOLARSHIP_FORM_INITIAL_VALUES,
  INSTITUTES,
  RELATIONS,
} from "../../data/scholarship-applications";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { useSnackbar } from "../../context/SnackbarContext";
import { extractApiErrorMessage } from "../../utils";

const uploadDocument = async (file: File): Promise<string> => {
  // This would be replaced with your actual API call
  const formData = new FormData();
  formData.append("file", file);
  const response = await api.post(
    "/scholarship-applications/documents/",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );

  return response.data.data.document_url;
};

const pkMobileRegex = /^(?:\+?92|0)?3[0-9]{2}[0-9]{7}$/;
const cnicRegex = /^(\d{5}-\d{7}-\d|\d{13})$/;
// Enhanced validation schema with array validation
const validationSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  cnic_number: Yup.string()
    .required("CNIC number is required")
    .matches(
      cnicRegex,
      "Enter CNIC as 12345-1234567-1 or 13 consecutive digits"
    ),
  gender: Yup.string().required("Gender is required"),
  age: Yup.number()
    .required("Age is required")
    .min(1, "Age must be positive and not zero"),
  residential_address: Yup.string().required("Residential address is required"),
  cell_phone: Yup.string()
    .required("Cell phone is required")
    .matches(pkMobileRegex, "Enter a valid Pakistani mobile number"),
  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),
  photo: Yup.string().required("Photo is required"),
  educational_history: Yup.array().of(
    Yup.object({
      institute: Yup.string().required("Institute is required"),
      score: Yup.number()
        .required("Score is required")
        .min(0, "Score cannot be negative")
        .max(100, "Score cannot exceed 100"),
      is_secondary_school: Yup.boolean(),
      is_higher_school: Yup.boolean(),
      is_university: Yup.boolean(),
      score_is_percentage: Yup.boolean(),
      current_semester: Yup.number().when("is_university", {
        is: true,
        then: (schema) =>
          schema
            .required("Semester is required")
            .min(0, "Cannot be negative"),
        otherwise: (schema) => schema.notRequired(),
      }),
      total_semesters: Yup.number().when("is_university", {
        is: true,
        then: (schema) =>
          schema.required("Total is required").min(1, "At least 1"),
        otherwise: (schema) => schema.notRequired(),
      }),
    }).test(
      "education-type-required",
      "Please select an education level",
      (value) =>
        value.is_secondary_school ||
        value.is_higher_school ||
        value.is_university
    )
  ),
  scholarship_request_details: Yup.object({
    degree_discipline: Yup.string().required("Degree discipline is required"),
    degree_name: Yup.string().required("Degree name is required"),
    degree_institute: Yup.string().required("Degree institute is required"),
    degree_semester: Yup.number()
      .required("Degree semester is required")
      .min(1, "Semester must be at least 1")
      .max(20, "Semester cannot exceed 20")
      .integer("Semester must be a whole number"),
    degree_recognized_by: Yup.string().required(
      "Recognition authority is required"
    ),
    tuition_fee_per_semester: Yup.number()
      .required("Tuition fee is required")
      .min(1, "Tuition fee cannot be negative or zero"),
    tuition_fee_per_semester_in_words: Yup.string().required(
      "Tuition fee in words is required"
    ),
    admission_fee: Yup.number()
      .required("Admission fee is required")
      .min(0, "Admission fee cannot be negative"),
  }),
  guardian_details: Yup.object({
    name: Yup.string().required("Father/Guardian name is required"),
    cnic_number: Yup.string()
      .required("Father/Guardian CNIC number is required")
      .matches(
        cnicRegex,
        "Enter CNIC as 12345-1234567-1 or 13 consecutive digits"
      ),
    cell_phone: Yup.string().required("Father/Guardian cell phone is required"),
    occupation: Yup.string().required("Father/Guardian occupation is required"),
    designation: Yup.string().required(
      "Father/Guardian designation is required"
    ),
    employer: Yup.string().required("Father/Guardian employer is required"),
    monthly_income: Yup.number()
      .required("Monthly income is required")
      .min(0, "Monthly income cannot be negative"),
    family_members_count: Yup.number()
      .required("Family member count is required")
      .min(1, "Cannot be zero"),
  }),
  accommodation_details: Yup.object({
    accommodation_type: Yup.string().required("Accommodation type is required"),
    accommodation_ownership: Yup.string().required(
      "Accommodation ownership is required"
    ),
  }),
  other_scholarships: Yup.array().of(
    Yup.object({
      institute_name: Yup.string().required("Institute name is required"),
      total_amount: Yup.number()
        .required("Amount is required")
        .min(0, "Amount cannot be negative"),
      duration_in_years: Yup.number()
        .required("Duration is required")
        .min(0, "Duration cannot be negative"),
      class_or_degree: Yup.string().required("Class or degree is required"),
    })
  ),
  studying_family_members: Yup.array().of(
    Yup.object({
      name: Yup.string().required("Name is required"),
      relation: Yup.string().required("Relation is required"),
      institute: Yup.string().required("Institute is required"),
      degree: Yup.string().required("Degree is required"),
      fee: Yup.number()
        .required("Fee is required")
        .min(0, "Fee cannot be negative"),
    })
  ),
  earning_family_members: Yup.array().of(
    Yup.object({
      name: Yup.string().required("Name is required"),
      relation: Yup.string().required("Relation is required"),
      organization: Yup.string().required("Organization is required"),
      occupation: Yup.string().required("Occupation is required"),
      income: Yup.number()
        .required("Income is required")
        .min(0, "Income cannot be negative"),
    })
  ),
  family_liabilities: Yup.array().of(
    Yup.object({
      nature: Yup.string().required("Nature of Liability is required"),
      amount: Yup.number()
        .required("Amount is required")
        .min(0, "Amount cannot be negative"),
      description: Yup.string().required("Description is required"),
    })
  ),
  family_expenses: Yup.array().of(
    Yup.object({
      nature: Yup.string().required("Nature is required"),
      amount: Yup.number()
        .required("Amount is required")
        .min(0, "Amount cannot be negative"),
      description: Yup.string().required("Description is required"),
    })
  ),
  personal_statements: Yup.array().of(
    Yup.object({
      question: Yup.string().required("Question is required"),
      response: Yup.string().required("Response is required"),
    })
  ),
  documents_attached: Yup.array().of(
    Yup.object({
      title: Yup.string().required("Document title is required"),
      attachment_url: Yup.array()
        .of(Yup.string().url("Invalid URL").required())
        .min(1, "At least one file is required"),
    })
  ),
});

// Custom colors
const colors = {
  primary: "#1e4d8c", // Deep blue
  secondary: "#3a7ca5", // Medium blue
  accent: "#f39c12", // Amber/gold
  success: "#2ecc71", // Green
  error: "#e74c3c", // Red
  background: "#f9fafb", // Light gray
  cardBg: "#ffffff", // White
  text: "#2c3e50", // Dark blue-gray
  lightText: "#7f8c8d", // Light gray
  border: "#e0e0e0", // Light gray border
  hover: "#edf2f7", // Light blue-gray hover
};

const SectionTitle = ({
  icon,
  title,
}: {
  icon: React.ReactNode;
  title: string;
}) => (
  <Box sx={{ display: "flex", alignItems: "center", mb: 3, mt: 4 }}>
    <Box sx={{ mr: 1.5, color: colors.primary, fontSize: "1.8rem" }}>
      {icon}
    </Box>
    <Typography
      variant="h5"
      sx={{ fontWeight: 600, color: colors.primary, letterSpacing: "0.5px" }}
    >
      {title}
    </Typography>
  </Box>
);

export default function ScholarshipApplicationForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const { showSnackbar } = useSnackbar();

  const [application, setApplication] =
    useState<ScholarshipApplicationForm | null>(null);
  const [draftData, setDraftData] = useState<ScholarshipApplicationForm | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  useEffect(() => {
    const fetchApplicationData = async () => {
      if (id) {
        // Edit mode - fetch existing application
        try {
          setIsLoading(true);
          const response = await api.get(`/scholarship-applications/${id}`);
          setApplication(response.data.data);
        } catch (error) {
          console.error("Error fetching application data:", error);
          Swal.fire({
            title: "Error!",
            text: extractApiErrorMessage(
              error,
              "Failed to load application data. Please try again later."
            ),
            icon: "error",
            confirmButtonText: "OK",
            confirmButtonColor: colors.error,
          });
        } finally {
          setIsLoading(false);
        }
      } else {
        // New application - check for draft data
        try {
          setIsLoading(true);
          const response = await api.get("/user-temporary-bucket");

          if (
            response.data.success &&
            response.data.data &&
            response.data.data.length > 0
          ) {
            const tempBucket = response.data.data.find(
              (item: any) =>
                item.record_type === "UNSUBMITTED_SCHOLARSHIP_APPLICATION"
            );

            if (tempBucket && tempBucket.record) {
              let parsedRecord;

              try {
                // Handle different record formats
                if (typeof tempBucket.record === "string") {
                  try {
                    // Try direct JSON parse first (if backend sends proper JSON)
                    parsedRecord = JSON.parse(tempBucket.record);
                  } catch (jsonError: any) {
                    // Parse Python dict format using eval
                    // Convert Python literals to JavaScript equivalents
                    const jsCode = tempBucket.record
                      .replace(/False/g, "false")
                      .replace(/True/g, "true")
                      .replace(/None/g, "null");

                    try {
                      // Use eval to parse Python dict (it works for JS object literals)
                      // eslint-disable-next-line no-eval
                      parsedRecord = eval("(" + jsCode + ")");
                    } catch (evalError: any) {
                      console.error("Error parsing draft data:", evalError);
                      throw evalError;
                    }
                  }
                } else {
                  parsedRecord = tempBucket.record;
                }

                if (parsedRecord && parsedRecord.scholarship_application_form) {
                  const draftFormData =
                    parsedRecord.scholarship_application_form;
                  setDraftData(draftFormData);

                  // Show snackbar notification
                  showSnackbar(
                    "Draft restored successfully! Your previous progress has been loaded.",
                    "success"
                  );
                }
              } catch (parseError) {
                console.error("Error parsing draft record:", parseError);
              }
            }
          }
        } catch (error) {
          console.error("Error fetching draft data:", error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchApplicationData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingDocuments, setUploadingDocuments] = useState<{
    [key: number]: boolean;
  }>({});
  const [errorMessage, setErrorMessage] = useState("");
  const [orgLogo, setOrgLogo] = useState<string | null>(null);
  const [initialFormValues] = useState<ScholarshipApplicationForm>(
    SCHOLARSHIP_FORM_INITIAL_VALUES
  );
  const [showFloatingSaveButton, setShowFloatingSaveButton] = useState(false);
  const formikRef = useRef<any>(null);

  // Show floating save button when user scrolls down
  useEffect(() => {
    const handleScroll = () => {
      // Show button when user scrolls more than 300px
      setShowFloatingSaveButton(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSubmit = async (values: ScholarshipApplicationForm) => {
    // Validate array entries before submission
    const hasEmptyArrays = false;

    // Check if any educational_history entries are incomplete
    if (values.educational_history.length > 0) {
      const incompleteEducation = values.educational_history.some(
        (edu) =>
          !edu.institute ||
          edu.score <= 0 ||
          (!edu.is_secondary_school &&
            !edu.is_higher_school &&
            !edu.is_university)
      );
      if (incompleteEducation) {
        Swal.fire({
          title: "Incomplete Information",
          text: "Please complete all education entries or remove incomplete ones.",
          icon: "error",
          confirmButtonText: "OK",
          confirmButtonColor: colors.error,
        });
        return;
      }
    }

    // Similar checks for other arrays
    if (values.other_scholarships.length > 0) {
      const incompleteScholarships = values.other_scholarships.some(
        (s) => !s.institute_name || !s.class_or_degree
      );
      if (incompleteScholarships) {
        Swal.fire({
          title: "Incomplete Information",
          text: "Please complete all scholarship entries or remove incomplete ones.",
          icon: "error",
          confirmButtonText: "OK",
          confirmButtonColor: colors.error,
        });
        return;
      }
    }

    // Show confirmation dialog before submission
    const confirmResult = await Swal.fire({
      title: "Confirm Submission",
      text: "Are you sure you want to submit this scholarship application?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: isEditMode ? "Yes, Update" : "Yes, Submit",
      cancelButtonText: "Cancel",
      confirmButtonColor: colors.primary,
      cancelButtonColor: "#6c757d",
    });

    // If user cancels, don't proceed
    if (!confirmResult.isConfirmed) {
      return;
    }

    // Continue with form submission if validation passes and user confirmed
    setLoading(true);
    try {
      // This would be your API call to submit the form

      let response;
      if (isEditMode) {
        // Update existing application
        response = await api.patch(`/scholarship-applications/${id}/`, values);
      } else {
        // Create new application
        response = await api.post("/scholarship-applications/", values);
      }

      // Show success dialog with SweetAlert2
      Swal.fire({
        title: "Success!",
        html: isEditMode
          ? "Your scholarship application has been updated successfully."
          : "Your scholarship application has been submitted successfully.",
        icon: "success",
        confirmButtonText: "OK",
        confirmButtonColor: colors.primary,
      }).then(() => {
        // Clear draft data after successful submission
        if (!isEditMode) {
          api
            .post("/user-temporary-bucket/", {
              record: {},
            })
            .catch((err) => console.error("Failed to clear draft:", err));
        }

        // Redirect based on flow
        if (isEditMode) {
          navigate("/applications");
        } else {
          navigate("/login");
        }
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      const message = extractApiErrorMessage(
        error,
        "There was an error submitting your application. Please try again later."
      );
      Swal.fire({
        title: "Error!",
        text: message,
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: colors.error,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async (values: ScholarshipApplicationForm) => {
    // Don't save draft in edit mode
    if (isEditMode) {
      showSnackbar(
        "Draft saving is only available for new applications. Your changes will be saved when you update the application.",
        "info"
      );
      return;
    }

    setIsSavingDraft(true);
    try {
      await api.post("/user-temporary-bucket/", {
        record: {
          scholarship_application_form: values,
        },
      });

      showSnackbar(
        "Draft saved successfully! You can continue filling it later.",
        "success"
      );
    } catch (error) {
      console.error("Error saving draft:", error);
      showSnackbar(
        extractApiErrorMessage(error, "Failed to save draft. Please try again."),
        "error"
      );
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handlePhotoUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingPhoto(true);
    try {
      const url = await uploadDocument(file);
      setFieldValue("photo", url);
    } catch (error) {
      console.error("Error uploading photo:", error);
      Swal.fire({
        title: "Upload Error",
        text: extractApiErrorMessage(error, "Error uploading photo. Please try again."),
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: colors.error,
      });
    } finally {
      setUploadingPhoto(false);
    }
  };


  const handleDocumentUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    docIndex: number,
    setFieldValue: (field: string, value: any) => void,
    values: ScholarshipApplicationForm
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingDocuments((prev) => ({ ...prev, [docIndex]: true }));
    try {
      const url = await uploadDocument(file);

      // clone → push url for that doc
      const docs = [...values.documents_attached];
      docs[docIndex] = {
        ...docs[docIndex],
        attachment_url: [...docs[docIndex].attachment_url, url],
      };
      setFieldValue("documents_attached", docs);
    } finally {
      setUploadingDocuments((prev) => ({ ...prev, [docIndex]: false }));
    }
  };

  return (
    <Container maxWidth={false} sx={{ py: 4, bgcolor: colors.background }}>
      <Paper
        elevation={3}
        sx={{
          p: 3,
          borderRadius: "12px",
          bgcolor: colors.cardBg,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        }}
      >
        {/* Header with logos */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            pb: 3,
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <FaGraduationCap size={40} color={colors.primary} />
            <Typography
              variant="h4"
              sx={{
                ml: 2,
                fontWeight: 700,
                color: colors.primary,
                letterSpacing: "0.5px",
              }}
            >
              Scholarship Application
            </Typography>
          </Box>
        </Box>

        {/* Info Banner for Draft Saving - Only for new applications */}
        {!isEditMode && (
          <Box
            sx={{
              mb: 4,
              p: 2.5,
              background:
                "linear-gradient(135deg, rgba(243, 156, 18, 0.08) 0%, rgba(243, 156, 18, 0.03) 100%)",
              borderLeft: `3px solid ${colors.accent}`,
              borderRadius: "8px",
              display: "flex",
              alignItems: "flex-start",
              gap: 2,
              boxShadow: "0 2px 8px rgba(243, 156, 18, 0.1)",
            }}
          >
            <Box
              sx={{
                bgcolor: colors.accent,
                color: "white",
                borderRadius: "50%",
                width: 32,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                mt: 0.2,
              }}
            >
              <FaInfoCircle size={16} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: colors.primary,
                  fontWeight: 600,
                  mb: 0.5,
                  letterSpacing: "0.3px",
                }}
              >
                Save Your Progress
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: colors.text,
                  lineHeight: 1.7,
                  fontSize: "0.9rem",
                }}
              >
                This application form is comprehensive and may take time to
                complete. Click the{" "}
                <strong style={{ color: colors.accent }}>Save Draft</strong>{" "}
                button at any time to save your progress. Your data will be
                securely saved and automatically restored when you return.
              </Typography>
            </Box>
          </Box>
        )}

        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
            <Typography variant="h6" color={colors.primary}>
              Loading application data...
            </Typography>
          </Box>
        )}

        {!isLoading && (
          <Formik
            innerRef={formikRef}
            initialValues={
              isEditMode && application
                ? application
                : draftData || SCHOLARSHIP_FORM_INITIAL_VALUES
            }
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              setFieldValue,
            }) => (
              <Form>
                <Box sx={{ maxWidth: "1200px", mx: "auto" }}>
                  {/* Personal Information */}
                  <SectionTitle
                    icon={<FaUserTie />}
                    title="Personal Information"
                  />
                  <Card
                    variant="outlined"
                    sx={{
                      mb: 4,
                      borderRadius: "10px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      border: `1px solid ${colors.border}`,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor: colors.primary,
                        py: 1,
                        px: 2,
                        borderTopLeftRadius: "10px",
                        borderTopRightRadius: "10px",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ color: "white", fontWeight: 500 }}
                      >
                        Enter your personal details
                      </Typography>
                    </Box>
                    <CardContent sx={{ pt: 3 }}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            id="name"
                            name="name"
                            label="Full Name"
                            value={values.name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.name && Boolean(errors.name)}
                            helperText={touched.name && errors.name}
                            variant="outlined"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&:hover fieldset": {
                                  borderColor: colors.secondary,
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: colors.primary,
                                },
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            id="cnic_number"
                            name="cnic_number"
                            label="CNIC Number"
                            value={values.cnic_number}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.cnic_number && Boolean(errors.cnic_number)
                            }
                            helperText={
                              touched.cnic_number && errors.cnic_number
                            }
                            variant="outlined"
                            placeholder="12345-1234567-1"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&:hover fieldset": {
                                  borderColor: colors.secondary,
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: colors.primary,
                                },
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                          <FormControl
                            fullWidth
                            error={touched.gender && Boolean(errors.gender)}
                            variant="outlined"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&:hover fieldset": {
                                  borderColor: colors.secondary,
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: colors.primary,
                                },
                              },
                            }}
                          >
                            <InputLabel id="gender-label">Gender</InputLabel>
                            <Select
                              labelId="gender-label"
                              id="gender"
                              name="gender"
                              value={values.gender}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              label="Gender"
                            >
                              <MenuItem value="MALE">Male</MenuItem>
                              <MenuItem value="FEMALE">Female</MenuItem>
                            </Select>
                            {touched.gender && errors.gender && (
                              <FormHelperText>{errors.gender}</FormHelperText>
                            )}
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            id="age"
                            name="age"
                            label="Age"
                            type="number"
                            value={values.age}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.age && Boolean(errors.age)}
                            helperText={touched.age && errors.age}
                            variant="outlined"
                            InputProps={{ inputProps: { min: 0 } }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&:hover fieldset": {
                                  borderColor: colors.secondary,
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: colors.primary,
                                },
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            id="email"
                            name="email"
                            label="Email"
                            type="email"
                            value={values.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.email && Boolean(errors.email)}
                            helperText={touched.email && errors.email}
                            variant="outlined"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&:hover fieldset": {
                                  borderColor: colors.secondary,
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: colors.primary,
                                },
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            id="cell_phone"
                            name="cell_phone"
                            label="Cell Phone"
                            value={values.cell_phone}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.cell_phone && Boolean(errors.cell_phone)
                            }
                            helperText={touched.cell_phone && errors.cell_phone}
                            variant="outlined"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&:hover fieldset": {
                                  borderColor: colors.secondary,
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: colors.primary,
                                },
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            id="telephone"
                            name="telephone"
                            label="Telephone (Optional)"
                            value={values.telephone}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.telephone && Boolean(errors.telephone)
                            }
                            helperText={touched.telephone && errors.telephone}
                            variant="outlined"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&:hover fieldset": {
                                  borderColor: colors.secondary,
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: colors.primary,
                                },
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            id="residential_address"
                            name="residential_address"
                            label="Residential Address"
                            value={values.residential_address}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.residential_address &&
                              Boolean(errors.residential_address)
                            }
                            helperText={
                              touched.residential_address &&
                              errors.residential_address
                            }
                            variant="outlined"
                            multiline
                            rows={2}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&:hover fieldset": {
                                  borderColor: colors.secondary,
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: colors.primary,
                                },
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <FormControl
                            fullWidth
                            error={touched.photo && Boolean(errors.photo)}
                          >
                            <InputLabel shrink htmlFor="photo-upload">
                              Photo
                            </InputLabel>
                            <Box sx={{ mt: 2 }}>
                              <input
                                accept="image/*"
                                id="photo-upload"
                                type="file"
                                style={{ display: "none" }}
                                onChange={(e) =>
                                  handlePhotoUpload(e, setFieldValue)
                                }
                              />
                              <label htmlFor="photo-upload">
                                <Button
                                  variant="outlined"
                                  component="span"
                                  startIcon={<FaUpload />}
                                  disabled={uploadingPhoto}
                                  sx={{
                                    borderColor: colors.secondary,
                                    color: colors.secondary,
                                    "&:hover": {
                                      borderColor: colors.primary,
                                      backgroundColor:
                                        "rgba(30, 77, 140, 0.04)",
                                    },
                                  }}
                                >
                                  {uploadingPhoto
                                    ? "Uploading..."
                                    : "Upload Photo"}
                                </Button>
                              </label>
                              {values.photo && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    mt: 1,
                                  }}
                                >
                                  <FaCheckCircle
                                    color={colors.success}
                                    style={{ marginRight: "8px" }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{ color: colors.text }}
                                  >
                                    Photo uploaded:{" "}
                                    {values.photo.split("/").pop()}
                                  </Typography>
                                </Box>
                              )}
                              {touched.photo && errors.photo && (
                                <FormHelperText error>
                                  {errors.photo}
                                </FormHelperText>
                              )}
                            </Box>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* Educational History */}
                  <SectionTitle
                    icon={<FaUserGraduate />}
                    title="Educational History"
                  />
                  <Card
                    variant="outlined"
                    sx={{
                      mb: 4,
                      borderRadius: "10px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      border: `1px solid ${colors.border}`,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor: colors.primary,
                        py: 1,
                        px: 2,
                        borderTopLeftRadius: "10px",
                        borderTopRightRadius: "10px",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ color: "white", fontWeight: 500 }}
                      >
                        Add your educational background (School + Intermediate +
                        University)
                      </Typography>
                    </Box>
                    <CardContent>
                      <FieldArray name="educational_history">
                        {({ push, remove }) => (
                          <Box>
                            {values.educational_history.length > 0 &&
                              values.educational_history.map(
                                (education, index) => (
                                  <Box
                                    key={index}
                                    sx={{
                                      mb: 3,
                                      p: 2,
                                      border: `1px solid ${colors.border}`,
                                      borderRadius: "8px",
                                      bgcolor:
                                        touched.educational_history?.[index] &&
                                        (errors.educational_history?.[
                                          index
                                        ] as any)
                                          ? "rgba(231, 76, 60, 0.05)"
                                          : "white",
                                      transition: "all 0.3s ease",
                                    }}
                                  >
                                    {/* ───────────────────────── Header ───────────────────────── */}
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        mb: 2,
                                      }}
                                    >
                                      <Typography
                                        variant="h6"
                                        sx={{
                                          color: colors.primary,
                                          fontWeight: 600,
                                        }}
                                      >
                                        Education #{index + 1}
                                      </Typography>
                                    </Box>

                                    <Grid container spacing={3}>
                                      <Grid item xs={12} sm={6}>
                                        <TextField
                                          fullWidth
                                          name={`educational_history.${index}.institute`}
                                          label="Institute"
                                          value={education.institute}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          error={
                                            touched.educational_history?.[index]
                                              ?.institute &&
                                            Boolean(
                                              (
                                                errors.educational_history?.[
                                                  index
                                                ] as any
                                              )?.institute
                                            )
                                          }
                                          helperText={
                                            touched.educational_history?.[index]
                                              ?.institute &&
                                            (
                                              errors.educational_history?.[
                                                index
                                              ] as any
                                            )?.institute
                                          }
                                          variant="outlined"
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              "&:hover fieldset": {
                                                borderColor: colors.secondary,
                                              },
                                              "&.Mui-focused fieldset": {
                                                borderColor: colors.primary,
                                              },
                                            },
                                          }}
                                        />
                                      </Grid>

                                      <Grid item xs={12} sm={6}>
                                        <FormControl component="fieldset">
                                          <FormLabel
                                            component="legend"
                                            sx={{ color: colors.text }}
                                          >
                                            Scoring System
                                          </FormLabel>
                                          <RadioGroup
                                            row
                                            name={`educational_history.${index}.score_is_percentage`}
                                            value={
                                              education.score_is_percentage
                                                ? "percentage"
                                                : "cgpa"
                                            }
                                            onChange={(e) =>
                                              setFieldValue(
                                                `educational_history.${index}.score_is_percentage`,
                                                e.target.value === "percentage"
                                              )
                                            }
                                          >
                                            <FormControlLabel
                                              value="percentage"
                                              control={
                                                <Radio
                                                  sx={{
                                                    color: colors.secondary,
                                                    "&.Mui-checked": {
                                                      color: colors.primary,
                                                    },
                                                  }}
                                                />
                                              }
                                              label="Percentage"
                                              sx={{ color: colors.text }}
                                            />
                                            <FormControlLabel
                                              value="cgpa"
                                              control={
                                                <Radio
                                                  sx={{
                                                    color: colors.secondary,
                                                    "&.Mui-checked": {
                                                      color: colors.primary,
                                                    },
                                                  }}
                                                />
                                              }
                                              label="CGPA"
                                              sx={{ color: colors.text }}
                                            />
                                          </RadioGroup>
                                        </FormControl>
                                      </Grid>

                                      <Grid item xs={12} sm={6}>
                                        <FormControl
                                          component="fieldset"
                                          error={
                                            touched.educational_history?.[
                                              index
                                            ] &&
                                            Boolean(
                                              errors.educational_history?.[
                                                index
                                              ] as any
                                            )
                                          }
                                        >
                                          <FormLabel
                                            component="legend"
                                            sx={{ color: colors.text }}
                                          >
                                            Education Level
                                          </FormLabel>
                                          <Grid container spacing={2}>
                                            {/* Secondary School */}
                                            <Grid item xs={12} sm={4}>
                                              <FormControlLabel
                                                control={
                                                  <Field
                                                    type="checkbox"
                                                    name={`educational_history.${index}.is_secondary_school`}
                                                    as={({ field }: any) => (
                                                      <Radio
                                                        {...field}
                                                        checked={
                                                          education.is_secondary_school
                                                        }
                                                        onChange={() => {
                                                          setFieldValue(
                                                            `educational_history.${index}.is_secondary_school`,
                                                            true
                                                          );
                                                          setFieldValue(
                                                            `educational_history.${index}.is_higher_school`,
                                                            false
                                                          );
                                                          setFieldValue(
                                                            `educational_history.${index}.is_university`,
                                                            false
                                                          );
                                                        }}
                                                        sx={{
                                                          color:
                                                            colors.secondary,
                                                          "&.Mui-checked": {
                                                            color:
                                                              colors.primary,
                                                          },
                                                        }}
                                                      />
                                                    )}
                                                  />
                                                }
                                                label="Secondary School"
                                                sx={{ color: colors.text }}
                                              />
                                            </Grid>

                                            {/* Higher School */}
                                            <Grid item xs={12} sm={4}>
                                              <FormControlLabel
                                                control={
                                                  <Field
                                                    type="checkbox"
                                                    name={`educational_history.${index}.is_higher_school`}
                                                    as={({ field }: any) => (
                                                      <Radio
                                                        {...field}
                                                        checked={
                                                          education.is_higher_school
                                                        }
                                                        onChange={() => {
                                                          setFieldValue(
                                                            `educational_history.${index}.is_secondary_school`,
                                                            false
                                                          );
                                                          setFieldValue(
                                                            `educational_history.${index}.is_higher_school`,
                                                            true
                                                          );
                                                          setFieldValue(
                                                            `educational_history.${index}.is_university`,
                                                            false
                                                          );
                                                        }}
                                                        sx={{
                                                          color:
                                                            colors.secondary,
                                                          "&.Mui-checked": {
                                                            color:
                                                              colors.primary,
                                                          },
                                                        }}
                                                      />
                                                    )}
                                                  />
                                                }
                                                label="Higher School"
                                                sx={{ color: colors.text }}
                                              />
                                            </Grid>

                                            {/* University */}
                                            <Grid item xs={12} sm={4}>
                                              <FormControlLabel
                                                control={
                                                  <Field
                                                    type="checkbox"
                                                    name={`educational_history.${index}.is_university`}
                                                    as={({ field }: any) => (
                                                      <Radio
                                                        {...field}
                                                        checked={
                                                          education.is_university
                                                        }
                                                        onChange={() => {
                                                          setFieldValue(
                                                            `educational_history.${index}.is_secondary_school`,
                                                            false
                                                          );
                                                          setFieldValue(
                                                            `educational_history.${index}.is_higher_school`,
                                                            false
                                                          );
                                                          setFieldValue(
                                                            `educational_history.${index}.is_university`,
                                                            true
                                                          );
                                                        }}
                                                        sx={{
                                                          color:
                                                            colors.secondary,
                                                          "&.Mui-checked": {
                                                            color:
                                                              colors.primary,
                                                          },
                                                        }}
                                                      />
                                                    )}
                                                  />
                                                }
                                                label="University"
                                                sx={{ color: colors.text }}
                                              />
                                            </Grid>
                                          </Grid>

                                          {/* validation helper */}
                                          {touched.educational_history?.[
                                            index
                                          ] &&
                                            (errors.educational_history?.[
                                              index
                                            ] as any) &&
                                            !(
                                              education.is_secondary_school ||
                                              education.is_higher_school ||
                                              education.is_university
                                            ) && (
                                              <FormHelperText error>
                                                Please select an education level
                                              </FormHelperText>
                                            )}
                                        </FormControl>
                                      </Grid>
                                      <Grid item xs={12} sm={6}>
                                        <TextField
                                          fullWidth
                                          name={`educational_history.${index}.score`}
                                          label={
                                            education.score_is_percentage
                                              ? "Percentage"
                                              : "CGPA"
                                          }
                                          type="number"
                                          value={education.score}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          error={
                                            touched.educational_history?.[index]
                                              ?.score &&
                                            Boolean(
                                              (
                                                errors.educational_history?.[
                                                  index
                                                ] as any
                                              )?.score
                                            )
                                          }
                                          helperText={
                                            touched.educational_history?.[index]
                                              ?.score &&
                                            (
                                              errors.educational_history?.[
                                                index
                                              ] as any
                                            )?.score
                                          }
                                          variant="outlined"
                                          InputProps={{
                                            endAdornment:
                                              education.score_is_percentage ? (
                                                <InputAdornment position="end">
                                                  %
                                                </InputAdornment>
                                              ) : null,
                                            inputProps:
                                              education.score_is_percentage
                                                ? { min: 0, max: 100 }
                                                : {
                                                    step: 0.01,
                                                    min: 0,
                                                    max: 4,
                                                  },
                                          }}
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              "&:hover fieldset": {
                                                borderColor: colors.secondary,
                                              },
                                              "&.Mui-focused fieldset": {
                                                borderColor: colors.primary,
                                              },
                                            },
                                          }}
                                        />
                                      </Grid>

                                      {education.is_university && (
                                        <>
                                          <Grid item xs={12} sm={6}>
                                            <TextField
                                              fullWidth
                                              name={`educational_history.${index}.current_semester`}
                                              label="Last Completed Semester"
                                              type="number"
                                              value={education.current_semester}
                                              onChange={handleChange}
                                              onBlur={handleBlur}
                                              error={
                                                touched.educational_history?.[
                                                  index
                                                ]?.current_semester &&
                                                Boolean(
                                                  (
                                                    errors.educational_history?.[
                                                      index
                                                    ] as any
                                                  )?.current_semester
                                                )
                                              }
                                              helperText={
                                                touched.educational_history?.[
                                                  index
                                                ]?.current_semester &&
                                                (
                                                  errors.educational_history?.[
                                                    index
                                                  ] as any
                                                )?.current_semester
                                              }
                                              variant="outlined"
                                              sx={{
                                                "& .MuiOutlinedInput-root": {
                                                  "&:hover fieldset": {
                                                    borderColor:
                                                      colors.secondary,
                                                  },
                                                  "&.Mui-focused fieldset": {
                                                    borderColor: colors.primary,
                                                  },
                                                },
                                              }}
                                            />
                                          </Grid>
                                          <Grid item xs={12} sm={6}>
                                            <TextField
                                              fullWidth
                                              name={`educational_history.${index}.total_semesters`}
                                              label="Total Semesters"
                                              type="number"
                                              value={education.total_semesters}
                                              onChange={handleChange}
                                              onBlur={handleBlur}
                                              error={
                                                touched.educational_history?.[
                                                  index
                                                ]?.total_semesters &&
                                                Boolean(
                                                  (
                                                    errors.educational_history?.[
                                                      index
                                                    ] as any
                                                  )?.total_semesters
                                                )
                                              }
                                              helperText={
                                                touched.educational_history?.[
                                                  index
                                                ]?.total_semesters &&
                                                (
                                                  errors.educational_history?.[
                                                    index
                                                  ] as any
                                                )?.total_semesters
                                              }
                                              variant="outlined"
                                              sx={{
                                                "& .MuiOutlinedInput-root": {
                                                  "&:hover fieldset": {
                                                    borderColor:
                                                      colors.secondary,
                                                  },
                                                  "&.Mui-focused fieldset": {
                                                    borderColor: colors.primary,
                                                  },
                                                },
                                              }}
                                            />
                                          </Grid>
                                        </>
                                      )}
                                    </Grid>
                                  </Box>
                                )
                              )}
                          </Box>
                        )}
                      </FieldArray>
                    </CardContent>
                  </Card>

                  {/* Scholarship Request Details */}
                  <SectionTitle
                    icon={<FaFileAlt />}
                    title="Scholarship Request Details"
                  />
                  <Card
                    variant="outlined"
                    sx={{
                      mb: 4,
                      borderRadius: "10px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      border: `1px solid ${colors.border}`,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor: colors.primary,
                        py: 1,
                        px: 2,
                        borderTopLeftRadius: "10px",
                        borderTopRightRadius: "10px",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ color: "white", fontWeight: 500 }}
                      >
                        Provide scholarship request information
                      </Typography>
                    </Box>
                    <CardContent>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            id="scholarship_request_details.degree_name"
                            name="scholarship_request_details.degree_name"
                            label="Degree Name"
                            value={
                              values.scholarship_request_details.degree_name
                            }
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.scholarship_request_details
                                ?.degree_name &&
                              Boolean(
                                errors.scholarship_request_details?.degree_name
                              )
                            }
                            helperText={
                              touched.scholarship_request_details
                                ?.degree_name &&
                              errors.scholarship_request_details?.degree_name
                            }
                            variant="outlined"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&:hover fieldset": {
                                  borderColor: colors.secondary,
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: colors.primary,
                                },
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            id="scholarship_request_details.degree_discipline"
                            name="scholarship_request_details.degree_discipline"
                            label="Degree Discipline"
                            value={
                              values.scholarship_request_details
                                .degree_discipline
                            }
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.scholarship_request_details
                                ?.degree_discipline &&
                              Boolean(
                                errors.scholarship_request_details
                                  ?.degree_discipline
                              )
                            }
                            helperText={
                              touched.scholarship_request_details
                                ?.degree_discipline &&
                              errors.scholarship_request_details
                                ?.degree_discipline
                            }
                            variant="outlined"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&:hover fieldset": {
                                  borderColor: colors.secondary,
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: colors.primary,
                                },
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Autocomplete
                            fullWidth
                            freeSolo
                            options={[...INSTITUTES, "Other"]}
                            value={
                              values.scholarship_request_details
                                .degree_institute === ""
                                ? null
                                : values.scholarship_request_details
                                    .degree_institute
                            }
                            onChange={(event, newValue) => {
                              if (newValue === "Other") {
                                setFieldValue(
                                  "scholarship_request_details.degree_institute",
                                  "OTHER"
                                );
                              } else {
                                setFieldValue(
                                  "scholarship_request_details.degree_institute",
                                  newValue || ""
                                );
                              }
                            }}
                            onInputChange={(event, newInputValue) => {
                              // Only update if not selecting "Other"
                              if (newInputValue !== "Other") {
                                setFieldValue(
                                  "scholarship_request_details.degree_institute",
                                  newInputValue
                                );
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                name="scholarship_request_details.degree_institute"
                                label="Degree Institute"
                                variant="outlined"
                                onBlur={handleBlur}
                                error={
                                  touched.scholarship_request_details
                                    ?.degree_institute &&
                                  Boolean(
                                    errors.scholarship_request_details
                                      ?.degree_institute
                                  )
                                }
                                helperText={
                                  touched.scholarship_request_details
                                    ?.degree_institute &&
                                  errors.scholarship_request_details
                                    ?.degree_institute
                                }
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    "&:hover fieldset": {
                                      borderColor: colors.secondary,
                                    },
                                    "&.Mui-focused fieldset": {
                                      borderColor: colors.primary,
                                    },
                                  },
                                }}
                              />
                            )}
                            sx={{
                              "& .MuiAutocomplete-inputRoot": {
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                  borderColor: colors.secondary,
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                  {
                                    borderColor: colors.primary,
                                  },
                              },
                            }}
                            noOptionsText="Type to add a custom institute"
                          />
                        </Grid>

                        {values.scholarship_request_details.degree_institute ===
                          "OTHER" && (
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              id="scholarship_request_details.degree_institute_custom"
                              name="scholarship_request_details.degree_institute_custom"
                              label="Enter Institute Name"
                              value={
                                values.scholarship_request_details
                                  .degree_institute_custom || ""
                              }
                              onChange={(e) => {
                                setFieldValue(
                                  "scholarship_request_details.degree_institute_custom",
                                  e.target.value
                                );
                                // Also update the main field with the custom value
                                setFieldValue(
                                  "scholarship_request_details.degree_institute",
                                  e.target.value
                                );
                              }}
                              onBlur={handleBlur}
                              error={
                                touched.scholarship_request_details
                                  ?.degree_institute &&
                                Boolean(
                                  errors.scholarship_request_details
                                    ?.degree_institute
                                )
                              }
                              helperText={
                                touched.scholarship_request_details
                                  ?.degree_institute &&
                                errors.scholarship_request_details
                                  ?.degree_institute
                              }
                              variant="outlined"
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  "&:hover fieldset": {
                                    borderColor: colors.secondary,
                                  },
                                  "&.Mui-focused fieldset": {
                                    borderColor: colors.primary,
                                  },
                                },
                              }}
                            />
                          </Grid>
                        )}

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            id="scholarship_request_details.degree_recognized_by"
                            name="scholarship_request_details.degree_recognized_by"
                            label="Recognized By"
                            value={
                              values.scholarship_request_details
                                .degree_recognized_by
                            }
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.scholarship_request_details
                                ?.degree_recognized_by &&
                              Boolean(
                                errors.scholarship_request_details
                                  ?.degree_recognized_by
                              )
                            }
                            helperText={
                              touched.scholarship_request_details
                                ?.degree_recognized_by &&
                              errors.scholarship_request_details
                                ?.degree_recognized_by
                            }
                            variant="outlined"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&:hover fieldset": {
                                  borderColor: colors.secondary,
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: colors.primary,
                                },
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            id="scholarship_request_details.degree_semester"
                            name="scholarship_request_details.degree_semester"
                            label="Applying for Semester"
                            type="number"
                            value={
                              values.scholarship_request_details.degree_semester
                            }
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.scholarship_request_details
                                ?.degree_semester &&
                              Boolean(
                                errors.scholarship_request_details
                                  ?.degree_semester
                              )
                            }
                            helperText={
                              touched.scholarship_request_details
                                ?.degree_semester &&
                              errors.scholarship_request_details
                                ?.degree_semester
                            }
                            variant="outlined"
                            InputProps={{
                              inputProps: { min: 1, max: 20 },
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&:hover fieldset": {
                                  borderColor: colors.secondary,
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: colors.primary,
                                },
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            id="scholarship_request_details.tuition_fee_per_semester"
                            name="scholarship_request_details.tuition_fee_per_semester"
                            label="Tuition Fee Per Semester"
                            type="number"
                            value={
                              values.scholarship_request_details
                                .tuition_fee_per_semester
                            }
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.scholarship_request_details
                                ?.tuition_fee_per_semester &&
                              Boolean(
                                errors.scholarship_request_details
                                  ?.tuition_fee_per_semester
                              )
                            }
                            helperText={
                              touched.scholarship_request_details
                                ?.tuition_fee_per_semester &&
                              errors.scholarship_request_details
                                ?.tuition_fee_per_semester
                            }
                            variant="outlined"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  PKR
                                </InputAdornment>
                              ),
                              inputProps: { min: 0 },
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&:hover fieldset": {
                                  borderColor: colors.secondary,
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: colors.primary,
                                },
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            id="scholarship_request_details.admission_fee"
                            name="scholarship_request_details.admission_fee"
                            label="Admission Fee"
                            type="number"
                            value={
                              values.scholarship_request_details.admission_fee
                            }
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.scholarship_request_details
                                ?.admission_fee &&
                              Boolean(
                                errors.scholarship_request_details
                                  ?.admission_fee
                              )
                            }
                            helperText={
                              touched.scholarship_request_details
                                ?.admission_fee &&
                              errors.scholarship_request_details?.admission_fee
                            }
                            variant="outlined"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  PKR
                                </InputAdornment>
                              ),
                              inputProps: { min: 0 },
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&:hover fieldset": {
                                  borderColor: colors.secondary,
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: colors.primary,
                                },
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            id="scholarship_request_details.tuition_fee_per_semester_in_words"
                            name="scholarship_request_details.tuition_fee_per_semester_in_words"
                            label="Tuition Fee Per Semester (In Words)"
                            value={
                              values.scholarship_request_details
                                .tuition_fee_per_semester_in_words
                            }
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.scholarship_request_details
                                ?.tuition_fee_per_semester_in_words &&
                              Boolean(
                                errors.scholarship_request_details
                                  ?.tuition_fee_per_semester_in_words
                              )
                            }
                            helperText={
                              touched.scholarship_request_details
                                ?.tuition_fee_per_semester_in_words &&
                              errors.scholarship_request_details
                                ?.tuition_fee_per_semester_in_words
                            }
                            variant="outlined"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&:hover fieldset": {
                                  borderColor: colors.secondary,
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: colors.primary,
                                },
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            id="scholarship_request_details.self_contribution_percentage"
                            name="scholarship_request_details.self_contribution_percentage"
                            label="How much tuition fee can you pay per semester? (%)"
                            type="number"
                            value={
                              values.scholarship_request_details
                                .self_contribution_percentage
                            }
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.scholarship_request_details
                                ?.self_contribution_percentage &&
                              Boolean(
                                errors.scholarship_request_details
                                  ?.self_contribution_percentage
                              )
                            }
                            helperText={
                              touched.scholarship_request_details
                                ?.self_contribution_percentage &&
                              errors.scholarship_request_details
                                ?.self_contribution_percentage
                            }
                            variant="outlined"
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  %
                                </InputAdornment>
                              ),
                              inputProps: { min: 0, max: 100 },
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&:hover fieldset": {
                                  borderColor: colors.secondary,
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: colors.primary,
                                },
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* Other Scholarships */}
                  <SectionTitle
                    icon={<FaMoneyBillWave />}
                    title="Other Scholarships"
                  />
                  <Card
                    variant="outlined"
                    sx={{
                      mb: 4,
                      borderRadius: "10px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      border: `1px solid ${colors.border}`,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor: colors.primary,
                        py: 1,
                        px: 2,
                        borderTopLeftRadius: "10px",
                        borderTopRightRadius: "10px",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ color: "white", fontWeight: 500 }}
                      >
                        Have you ever been awarded any scholarship/loan before?
                      </Typography>
                    </Box>
                    <CardContent>
                      <FieldArray name="other_scholarships">
                        {({ push, remove }) => (
                          <Box>
                            {values.other_scholarships.length > 0 &&
                              values.other_scholarships.map(
                                (scholarship, index) => (
                                  <Box
                                    key={index}
                                    sx={{
                                      mb: 3,
                                      p: 2,
                                      border: `1px solid ${colors.border}`,
                                      borderRadius: "8px",
                                      bgcolor:
                                        touched.other_scholarships?.[index] &&
                                        (errors.other_scholarships?.[
                                          index
                                        ] as any)
                                          ? "rgba(231, 76, 60, 0.05)"
                                          : "white",
                                      transition: "all 0.3s ease",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        mb: 2,
                                      }}
                                    >
                                      <Typography
                                        variant="h6"
                                        sx={{
                                          color: colors.primary,
                                          fontWeight: 600,
                                        }}
                                      >
                                        Scholarship #{index + 1}
                                      </Typography>
                                      <IconButton
                                        onClick={() => remove(index)}
                                        color="error"
                                        size="small"
                                        sx={{
                                          color: colors.error,
                                          "&:hover": {
                                            bgcolor: "rgba(231, 76, 60, 0.1)",
                                          },
                                        }}
                                      >
                                        <FaTrash />
                                      </IconButton>
                                    </Box>

                                    <Grid container spacing={3}>
                                      <Grid item xs={12} sm={6}>
                                        <TextField
                                          fullWidth
                                          name={`other_scholarships.${index}.institute_name`}
                                          label="Scholarship/Loan organization"
                                          value={scholarship.institute_name}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          error={
                                            touched.other_scholarships?.[index]
                                              ?.institute_name &&
                                            Boolean(
                                              (
                                                errors.other_scholarships?.[
                                                  index
                                                ] as any
                                              )?.institute_name
                                            )
                                          }
                                          helperText={
                                            touched.other_scholarships?.[index]
                                              ?.institute_name &&
                                            (
                                              errors.other_scholarships?.[
                                                index
                                              ] as any
                                            )?.institute_name
                                          }
                                          variant="outlined"
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              "&:hover fieldset": {
                                                borderColor: colors.secondary,
                                              },
                                              "&.Mui-focused fieldset": {
                                                borderColor: colors.primary,
                                              },
                                            },
                                          }}
                                        />
                                      </Grid>

                                      <Grid item xs={12} sm={6}>
                                        <TextField
                                          fullWidth
                                          name={`other_scholarships.${index}.class_or_degree`}
                                          label="Class or Degree"
                                          value={scholarship.class_or_degree}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          error={
                                            touched.other_scholarships?.[index]
                                              ?.class_or_degree &&
                                            Boolean(
                                              (
                                                errors.other_scholarships?.[
                                                  index
                                                ] as any
                                              )?.class_or_degree
                                            )
                                          }
                                          helperText={
                                            touched.other_scholarships?.[index]
                                              ?.class_or_degree &&
                                            (
                                              errors.other_scholarships?.[
                                                index
                                              ] as any
                                            )?.class_or_degree
                                          }
                                          variant="outlined"
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              "&:hover fieldset": {
                                                borderColor: colors.secondary,
                                              },
                                              "&.Mui-focused fieldset": {
                                                borderColor: colors.primary,
                                              },
                                            },
                                          }}
                                        />
                                      </Grid>

                                      <Grid item xs={12} sm={6}>
                                        <TextField
                                          fullWidth
                                          name={`other_scholarships.${index}.total_amount`}
                                          label="Total Amount"
                                          type="number"
                                          value={scholarship.total_amount}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          error={
                                            touched.other_scholarships?.[index]
                                              ?.total_amount &&
                                            Boolean(
                                              (
                                                errors.other_scholarships?.[
                                                  index
                                                ] as any
                                              )?.total_amount
                                            )
                                          }
                                          helperText={
                                            touched.other_scholarships?.[index]
                                              ?.total_amount &&
                                            (
                                              errors.other_scholarships?.[
                                                index
                                              ] as any
                                            )?.total_amount
                                          }
                                          variant="outlined"
                                          InputProps={{
                                            startAdornment: (
                                              <InputAdornment position="start">
                                                PKR
                                              </InputAdornment>
                                            ),
                                            inputProps: { min: 0 },
                                          }}
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              "&:hover fieldset": {
                                                borderColor: colors.secondary,
                                              },
                                              "&.Mui-focused fieldset": {
                                                borderColor: colors.primary,
                                              },
                                            },
                                          }}
                                        />
                                      </Grid>

                                      <Grid item xs={12} sm={6}>
                                        <TextField
                                          fullWidth
                                          name={`other_scholarships.${index}.duration_in_years`}
                                          label="Duration (Years)"
                                          type="number"
                                          value={scholarship.duration_in_years}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          error={
                                            touched.other_scholarships?.[index]
                                              ?.duration_in_years &&
                                            Boolean(
                                              (
                                                errors.other_scholarships?.[
                                                  index
                                                ] as any
                                              )?.duration_in_years
                                            )
                                          }
                                          helperText={
                                            touched.other_scholarships?.[index]
                                              ?.duration_in_years &&
                                            (
                                              errors.other_scholarships?.[
                                                index
                                              ] as any
                                            )?.duration_in_years
                                          }
                                          variant="outlined"
                                          InputProps={{
                                            endAdornment: (
                                              <InputAdornment position="end">
                                                years
                                              </InputAdornment>
                                            ),
                                            inputProps: { min: 0, step: 0.5 },
                                          }}
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              "&:hover fieldset": {
                                                borderColor: colors.secondary,
                                              },
                                              "&.Mui-focused fieldset": {
                                                borderColor: colors.primary,
                                              },
                                            },
                                          }}
                                        />
                                      </Grid>
                                    </Grid>
                                  </Box>
                                )
                              )}

                            <Button
                              type="button"
                              variant="outlined"
                              startIcon={<FaPlus />}
                              onClick={() =>
                                push({
                                  institute_name: "",
                                  total_amount: 0,
                                  duration_in_years: 0,
                                  class_or_degree: "",
                                })
                              }
                              sx={{
                                mt: 2,
                                borderColor: colors.secondary,
                                color: colors.secondary,
                                "&:hover": {
                                  borderColor: colors.primary,
                                  backgroundColor: "rgba(30, 77, 140, 0.04)",
                                },
                              }}
                            >
                              Add Scholarship
                            </Button>
                          </Box>
                        )}
                      </FieldArray>
                    </CardContent>
                  </Card>

                  {/* Guardian Details */}
                  <SectionTitle icon={<FaUserTie />} title="Guardian Details" />
                  <Card
                    variant="outlined"
                    sx={{
                      mb: 4,
                      borderRadius: "10px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      border: `1px solid ${colors.border}`,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor: colors.primary,
                        py: 1,
                        px: 2,
                        borderTopLeftRadius: "10px",
                        borderTopRightRadius: "10px",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ color: "white", fontWeight: 500 }}
                      >
                        Provide father/guardian information
                      </Typography>
                    </Box>
                    <CardContent>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            id="guardian_details.name"
                            name="guardian_details.name"
                            label="Father/Guardian Name"
                            value={values.guardian_details.name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.guardian_details?.name &&
                              Boolean(errors.guardian_details?.name)
                            }
                            helperText={
                              touched.guardian_details?.name &&
                              errors.guardian_details?.name
                            }
                            variant="outlined"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&:hover fieldset": {
                                  borderColor: colors.secondary,
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: colors.primary,
                                },
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            id="guardian_details.cnic_number"
                            name="guardian_details.cnic_number"
                            label="Father/Guardian CNIC Number"
                            value={values.guardian_details.cnic_number}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.guardian_details?.cnic_number &&
                              Boolean(errors.guardian_details?.cnic_number)
                            }
                            helperText={
                              touched.guardian_details?.cnic_number &&
                              errors.guardian_details?.cnic_number
                            }
                            variant="outlined"
                            placeholder="12345-1234567-1"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&:hover fieldset": {
                                  borderColor: colors.secondary,
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: colors.primary,
                                },
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            id="guardian_details.cell_phone"
                            name="guardian_details.cell_phone"
                            label="Father/Guardian Cell Phone"
                            value={values.guardian_details.cell_phone}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.guardian_details?.cell_phone &&
                              Boolean(errors.guardian_details?.cell_phone)
                            }
                            helperText={
                              touched.guardian_details?.cell_phone &&
                              errors.guardian_details?.cell_phone
                            }
                            variant="outlined"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&:hover fieldset": {
                                  borderColor: colors.secondary,
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: colors.primary,
                                },
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            id="guardian_details.occupation"
                            name="guardian_details.occupation"
                            label="Father/Guardian Occupation"
                            value={values.guardian_details.occupation}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.guardian_details?.occupation &&
                              Boolean(errors.guardian_details?.occupation)
                            }
                            helperText={
                              touched.guardian_details?.occupation &&
                              errors.guardian_details?.occupation
                            }
                            variant="outlined"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&:hover fieldset": {
                                  borderColor: colors.secondary,
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: colors.primary,
                                },
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            id="guardian_details.designation"
                            name="guardian_details.designation"
                            label="Father/Guardian Designation"
                            value={values.guardian_details.designation}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.guardian_details?.designation &&
                              Boolean(errors.guardian_details?.designation)
                            }
                            helperText={
                              touched.guardian_details?.designation &&
                              errors.guardian_details?.designation
                            }
                            variant="outlined"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&:hover fieldset": {
                                  borderColor: colors.secondary,
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: colors.primary,
                                },
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            id="guardian_details.employer"
                            name="guardian_details.employer"
                            label="Father/Guardian Employer"
                            value={values.guardian_details.employer}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.guardian_details?.employer &&
                              Boolean(errors.guardian_details?.employer)
                            }
                            helperText={
                              touched.guardian_details?.employer &&
                              errors.guardian_details?.employer
                            }
                            variant="outlined"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&:hover fieldset": {
                                  borderColor: colors.secondary,
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: colors.primary,
                                },
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            id="guardian_details.monthly_income"
                            name="guardian_details.monthly_income"
                            label="Monthly Income"
                            type="number"
                            value={values.guardian_details.monthly_income}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.guardian_details?.monthly_income &&
                              Boolean(errors.guardian_details?.monthly_income)
                            }
                            helperText={
                              touched.guardian_details?.monthly_income &&
                              errors.guardian_details?.monthly_income
                            }
                            variant="outlined"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  PKR
                                </InputAdornment>
                              ),
                              inputProps: { min: 0 },
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&:hover fieldset": {
                                  borderColor: colors.secondary,
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: colors.primary,
                                },
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            id="guardian_details.pension"
                            name="guardian_details.pension"
                            label="Pension (if any)"
                            type="number"
                            value={values.guardian_details.pension}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.guardian_details?.pension &&
                              Boolean(errors.guardian_details?.pension)
                            }
                            helperText={
                              touched.guardian_details?.pension &&
                              errors.guardian_details?.pension
                            }
                            variant="outlined"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  PKR
                                </InputAdornment>
                              ),
                              inputProps: { min: 0 },
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&:hover fieldset": {
                                  borderColor: colors.secondary,
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: colors.primary,
                                },
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            id="guardian_details.family_members_count"
                            name="guardian_details.family_members_count"
                            label="Family Members Count"
                            type="number"
                            value={values.guardian_details.family_members_count}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.guardian_details?.family_members_count &&
                              Boolean(
                                errors.guardian_details?.family_members_count
                              )
                            }
                            helperText={
                              touched.guardian_details?.family_members_count &&
                              errors.guardian_details?.family_members_count
                            }
                            variant="outlined"
                            InputProps={{
                              inputProps: { min: 0 },
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&:hover fieldset": {
                                  borderColor: colors.secondary,
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: colors.primary,
                                },
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* Family Members */}
                  <SectionTitle icon={<FaUsers />} title="Family Members" />

                  {/* Studying Family Members */}
                  <Typography
                    variant="h6"
                    sx={{
                      mt: 2,
                      mb: 2,
                      fontWeight: 600,
                      color: colors.secondary,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <FaUserGraduate
                      style={{ marginRight: "8px", fontSize: "1rem" }}
                    />
                    Studying Family Members
                  </Typography>
                  <Card
                    variant="outlined"
                    sx={{
                      mb: 4,
                      borderRadius: "10px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      border: `1px solid ${colors.border}`,
                      overflow: "hidden",
                    }}
                  >
                    <CardContent>
                      <FieldArray name="studying_family_members">
                        {({ push, remove }) => (
                          <Box>
                            {values.studying_family_members.length > 0 &&
                              values.studying_family_members.map(
                                (member, index) => (
                                  <Box
                                    key={index}
                                    sx={{
                                      mb: 3,
                                      p: 2,
                                      border: `1px solid ${colors.border}`,
                                      borderRadius: "8px",
                                      bgcolor:
                                        touched.studying_family_members?.[
                                          index
                                        ] &&
                                        (errors.studying_family_members?.[
                                          index
                                        ] as any)
                                          ? "rgba(231, 76, 60, 0.05)"
                                          : "white",
                                      transition: "all 0.3s ease",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        mb: 2,
                                      }}
                                    >
                                      <Typography
                                        variant="subtitle1"
                                        sx={{
                                          color: colors.primary,
                                          fontWeight: 600,
                                        }}
                                      >
                                        Studying Member #{index + 1}
                                      </Typography>
                                      <IconButton
                                        onClick={() => remove(index)}
                                        color="error"
                                        size="small"
                                        sx={{
                                          color: colors.error,
                                          "&:hover": {
                                            bgcolor: "rgba(231, 76, 60, 0.1)",
                                          },
                                        }}
                                      >
                                        <FaTrash />
                                      </IconButton>
                                    </Box>

                                    <Grid container spacing={3}>
                                      <Grid item xs={12} sm={6}>
                                        <TextField
                                          fullWidth
                                          name={`studying_family_members.${index}.name`}
                                          label="Name"
                                          value={member.name}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          error={
                                            touched.studying_family_members?.[
                                              index
                                            ]?.name &&
                                            Boolean(
                                              (
                                                errors
                                                  .studying_family_members?.[
                                                  index
                                                ] as any
                                              )?.name
                                            )
                                          }
                                          helperText={
                                            touched.studying_family_members?.[
                                              index
                                            ]?.name &&
                                            (
                                              errors.studying_family_members?.[
                                                index
                                              ] as any
                                            )?.name
                                          }
                                          variant="outlined"
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              "&:hover fieldset": {
                                                borderColor: colors.secondary,
                                              },
                                              "&.Mui-focused fieldset": {
                                                borderColor: colors.primary,
                                              },
                                            },
                                          }}
                                        />
                                      </Grid>

                                      <Grid item xs={12} sm={6}>
                                        <FormControl
                                          fullWidth
                                          variant="outlined"
                                        >
                                          <InputLabel
                                            id={`relation-label-${index}`}
                                          >
                                            Relation
                                          </InputLabel>

                                          <Select
                                            labelId={`relation-label-${index}`}
                                            id={`studying_family_members.${index}.relation`}
                                            name={`studying_family_members.${index}.relation`}
                                            label="Relation"
                                            value={member.relation}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={
                                              touched.studying_family_members?.[
                                                index
                                              ]?.relation &&
                                              Boolean(
                                                (
                                                  errors
                                                    .studying_family_members?.[
                                                    index
                                                  ] as any
                                                )?.relation
                                              )
                                            }
                                            sx={{
                                              "& .MuiOutlinedInput-root": {
                                                "&:hover fieldset": {
                                                  borderColor: colors.secondary,
                                                },
                                                "&.Mui-focused fieldset": {
                                                  borderColor: colors.primary,
                                                },
                                              },
                                            }}
                                          >
                                            {RELATIONS.map((rel) => (
                                              <MenuItem key={rel} value={rel}>
                                                {rel}
                                              </MenuItem>
                                            ))}
                                          </Select>

                                          <FormHelperText sx={{ color: "red" }}>
                                            {touched.studying_family_members?.[
                                              index
                                            ]?.relation &&
                                              (
                                                errors
                                                  .studying_family_members?.[
                                                  index
                                                ] as any
                                              )?.relation}
                                          </FormHelperText>
                                        </FormControl>
                                      </Grid>

                                      <Grid item xs={12} sm={6}>
                                        <TextField
                                          fullWidth
                                          name={`studying_family_members.${index}.institute`}
                                          label="Institute"
                                          value={member.institute}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          error={
                                            touched.studying_family_members?.[
                                              index
                                            ]?.institute &&
                                            Boolean(
                                              (
                                                errors
                                                  .studying_family_members?.[
                                                  index
                                                ] as any
                                              )?.institute
                                            )
                                          }
                                          helperText={
                                            touched.studying_family_members?.[
                                              index
                                            ]?.institute &&
                                            (
                                              errors.studying_family_members?.[
                                                index
                                              ] as any
                                            )?.institute
                                          }
                                          variant="outlined"
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              "&:hover fieldset": {
                                                borderColor: colors.secondary,
                                              },
                                              "&.Mui-focused fieldset": {
                                                borderColor: colors.primary,
                                              },
                                            },
                                          }}
                                        />
                                      </Grid>

                                      <Grid item xs={12} sm={6}>
                                        <TextField
                                          fullWidth
                                          name={`studying_family_members.${index}.degree`}
                                          label="Degree"
                                          value={member.degree}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          error={
                                            touched.studying_family_members?.[
                                              index
                                            ]?.degree &&
                                            Boolean(
                                              (
                                                errors
                                                  .studying_family_members?.[
                                                  index
                                                ] as any
                                              )?.degree
                                            )
                                          }
                                          helperText={
                                            touched.studying_family_members?.[
                                              index
                                            ]?.degree &&
                                            (
                                              errors.studying_family_members?.[
                                                index
                                              ] as any
                                            )?.degree
                                          }
                                          variant="outlined"
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              "&:hover fieldset": {
                                                borderColor: colors.secondary,
                                              },
                                              "&.Mui-focused fieldset": {
                                                borderColor: colors.primary,
                                              },
                                            },
                                          }}
                                        />
                                      </Grid>

                                      <Grid item xs={12} sm={6}>
                                        <TextField
                                          fullWidth
                                          name={`studying_family_members.${index}.fee`}
                                          label="Fee"
                                          type="number"
                                          value={member.fee}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          error={
                                            touched.studying_family_members?.[
                                              index
                                            ]?.fee &&
                                            Boolean(
                                              (
                                                errors
                                                  .studying_family_members?.[
                                                  index
                                                ] as any
                                              )?.fee
                                            )
                                          }
                                          helperText={
                                            touched.studying_family_members?.[
                                              index
                                            ]?.fee &&
                                            (
                                              errors.studying_family_members?.[
                                                index
                                              ] as any
                                            )?.fee
                                          }
                                          variant="outlined"
                                          InputProps={{
                                            startAdornment: (
                                              <InputAdornment position="start">
                                                PKR
                                              </InputAdornment>
                                            ),
                                            inputProps: { min: 0 },
                                          }}
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              "&:hover fieldset": {
                                                borderColor: colors.secondary,
                                              },
                                              "&.Mui-focused fieldset": {
                                                borderColor: colors.primary,
                                              },
                                            },
                                          }}
                                        />
                                      </Grid>
                                    </Grid>
                                  </Box>
                                )
                              )}

                            <Button
                              type="button"
                              variant="outlined"
                              startIcon={<FaPlus />}
                              onClick={() =>
                                push({
                                  relation: "",
                                  institute: "",
                                  degree: "",
                                  fee: 0,
                                })
                              }
                              sx={{
                                mt: 2,
                                borderColor: colors.secondary,
                                color: colors.secondary,
                                "&:hover": {
                                  borderColor: colors.primary,
                                  backgroundColor: "rgba(30, 77, 140, 0.04)",
                                },
                              }}
                            >
                              Add Studying Member
                            </Button>
                          </Box>
                        )}
                      </FieldArray>
                    </CardContent>
                  </Card>

                  {/* Earning Family Members */}
                  <Typography
                    variant="h6"
                    sx={{
                      mt: 2,
                      mb: 2,
                      fontWeight: 600,
                      color: colors.secondary,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <FaMoneyBillWave
                      style={{ marginRight: "8px", fontSize: "1rem" }}
                    />
                    Earning Family Members
                  </Typography>
                  <Card
                    variant="outlined"
                    sx={{
                      mb: 4,
                      borderRadius: "10px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      border: `1px solid ${colors.border}`,
                      overflow: "hidden",
                    }}
                  >
                    <CardContent>
                      <FieldArray name="earning_family_members">
                        {({ push, remove }) => (
                          <Box>
                            {values.earning_family_members.length > 0 &&
                              values.earning_family_members.map(
                                (member, index) => (
                                  <Box
                                    key={index}
                                    sx={{
                                      mb: 3,
                                      p: 2,
                                      border: `1px solid ${colors.border}`,
                                      borderRadius: "8px",
                                      bgcolor:
                                        touched.earning_family_members?.[
                                          index
                                        ] &&
                                        (errors.earning_family_members?.[
                                          index
                                        ] as any)
                                          ? "rgba(231, 76, 60, 0.05)"
                                          : "white",
                                      transition: "all 0.3s ease",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        mb: 2,
                                      }}
                                    >
                                      <Typography
                                        variant="subtitle1"
                                        sx={{
                                          color: colors.primary,
                                          fontWeight: 600,
                                        }}
                                      >
                                        Earning Member #{index + 1}
                                      </Typography>
                                      <IconButton
                                        onClick={() => remove(index)}
                                        color="error"
                                        size="small"
                                        sx={{
                                          color: colors.error,
                                          "&:hover": {
                                            bgcolor: "rgba(231, 76, 60, 0.1)",
                                          },
                                        }}
                                      >
                                        <FaTrash />
                                      </IconButton>
                                    </Box>

                                    <Grid container spacing={3}>
                                      <Grid item xs={12} sm={6}>
                                        <TextField
                                          fullWidth
                                          name={`earning_family_members.${index}.name`}
                                          label="Name"
                                          value={member.name}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          error={
                                            touched.earning_family_members?.[
                                              index
                                            ]?.name &&
                                            Boolean(
                                              (
                                                errors.earning_family_members?.[
                                                  index
                                                ] as any
                                              )?.name
                                            )
                                          }
                                          helperText={
                                            touched.earning_family_members?.[
                                              index
                                            ]?.name &&
                                            (
                                              errors.earning_family_members?.[
                                                index
                                              ] as any
                                            )?.name
                                          }
                                          variant="outlined"
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              "&:hover fieldset": {
                                                borderColor: colors.secondary,
                                              },
                                              "&.Mui-focused fieldset": {
                                                borderColor: colors.primary,
                                              },
                                            },
                                          }}
                                        />
                                      </Grid>

                                      <Grid item xs={12} sm={6}>
                                        <FormControl
                                          fullWidth
                                          variant="outlined"
                                        >
                                          <InputLabel
                                            id={`relation-label-${index}`}
                                          >
                                            Relation
                                          </InputLabel>

                                          <Select
                                            labelId={`relation-label-${index}`}
                                            id={`earning_family_members.${index}.relation`}
                                            name={`earning_family_members.${index}.relation`}
                                            label="Relation"
                                            value={member.relation}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={
                                              touched.earning_family_members?.[
                                                index
                                              ]?.relation &&
                                              Boolean(
                                                (
                                                  errors
                                                    .earning_family_members?.[
                                                    index
                                                  ] as any
                                                )?.relation
                                              )
                                            }
                                            sx={{
                                              "& .MuiOutlinedInput-root": {
                                                "&:hover fieldset": {
                                                  borderColor: colors.secondary,
                                                },
                                                "&.Mui-focused fieldset": {
                                                  borderColor: colors.primary,
                                                },
                                              },
                                            }}
                                          >
                                            {RELATIONS.map((rel) => (
                                              <MenuItem key={rel} value={rel}>
                                                {rel}
                                              </MenuItem>
                                            ))}
                                          </Select>

                                          <FormHelperText sx={{ color: "red" }}>
                                            {touched.earning_family_members?.[
                                              index
                                            ]?.relation &&
                                              (
                                                errors.earning_family_members?.[
                                                  index
                                                ] as any
                                              )?.relation}
                                          </FormHelperText>
                                        </FormControl>
                                      </Grid>

                                      <Grid item xs={12} sm={6}>
                                        <TextField
                                          fullWidth
                                          name={`earning_family_members.${index}.organization`}
                                          label="Organization"
                                          value={member.organization}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          error={
                                            touched.earning_family_members?.[
                                              index
                                            ]?.organization &&
                                            Boolean(
                                              (
                                                errors.earning_family_members?.[
                                                  index
                                                ] as any
                                              )?.organization
                                            )
                                          }
                                          helperText={
                                            touched.earning_family_members?.[
                                              index
                                            ]?.organization &&
                                            (
                                              errors.earning_family_members?.[
                                                index
                                              ] as any
                                            )?.organization
                                          }
                                          variant="outlined"
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              "&:hover fieldset": {
                                                borderColor: colors.secondary,
                                              },
                                              "&.Mui-focused fieldset": {
                                                borderColor: colors.primary,
                                              },
                                            },
                                          }}
                                        />
                                      </Grid>

                                      <Grid item xs={12} sm={6}>
                                        <TextField
                                          fullWidth
                                          name={`earning_family_members.${index}.occupation`}
                                          label="Occupation"
                                          value={member.occupation}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          error={
                                            touched.earning_family_members?.[
                                              index
                                            ]?.occupation &&
                                            Boolean(
                                              (
                                                errors.earning_family_members?.[
                                                  index
                                                ] as any
                                              )?.occupation
                                            )
                                          }
                                          helperText={
                                            touched.earning_family_members?.[
                                              index
                                            ]?.occupation &&
                                            (
                                              errors.earning_family_members?.[
                                                index
                                              ] as any
                                            )?.occupation
                                          }
                                          variant="outlined"
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              "&:hover fieldset": {
                                                borderColor: colors.secondary,
                                              },
                                              "&.Mui-focused fieldset": {
                                                borderColor: colors.primary,
                                              },
                                            },
                                          }}
                                        />
                                      </Grid>

                                      <Grid item xs={12} sm={6}>
                                        <TextField
                                          fullWidth
                                          name={`earning_family_members.${index}.income`}
                                          label="Income"
                                          type="number"
                                          value={member.income}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          error={
                                            touched.earning_family_members?.[
                                              index
                                            ]?.income &&
                                            Boolean(
                                              (
                                                errors.earning_family_members?.[
                                                  index
                                                ] as any
                                              )?.income
                                            )
                                          }
                                          helperText={
                                            touched.earning_family_members?.[
                                              index
                                            ]?.income &&
                                            (
                                              errors.earning_family_members?.[
                                                index
                                              ] as any
                                            )?.income
                                          }
                                          variant="outlined"
                                          InputProps={{
                                            startAdornment: (
                                              <InputAdornment position="start">
                                                PKR
                                              </InputAdornment>
                                            ),
                                            inputProps: { min: 0 },
                                          }}
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              "&:hover fieldset": {
                                                borderColor: colors.secondary,
                                              },
                                              "&.Mui-focused fieldset": {
                                                borderColor: colors.primary,
                                              },
                                            },
                                          }}
                                        />
                                      </Grid>
                                    </Grid>
                                  </Box>
                                )
                              )}

                            <Button
                              type="button"
                              variant="outlined"
                              startIcon={<FaPlus />}
                              onClick={() =>
                                push({
                                  relation: "",
                                  organization: "",
                                  occupation: "",
                                  income: 0,
                                })
                              }
                              sx={{
                                mt: 2,
                                borderColor: colors.secondary,
                                color: colors.secondary,
                                "&:hover": {
                                  borderColor: colors.primary,
                                  backgroundColor: "rgba(30, 77, 140, 0.04)",
                                },
                              }}
                            >
                              Add Earning Member
                            </Button>
                          </Box>
                        )}
                      </FieldArray>
                    </CardContent>
                  </Card>

                  {/* Accommodation Details */}
                  <SectionTitle
                    icon={<FaHome />}
                    title="Accommodation Details"
                  />
                  <Card
                    variant="outlined"
                    sx={{
                      mb: 4,
                      borderRadius: "10px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      border: `1px solid ${colors.border}`,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor: colors.primary,
                        py: 1,
                        px: 2,
                        borderTopLeftRadius: "10px",
                        borderTopRightRadius: "10px",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ color: "white", fontWeight: 500 }}
                      >
                        Provide accommodation details
                      </Typography>
                    </Box>
                    <CardContent>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <FormControl
                            fullWidth
                            variant="outlined"
                            error={
                              touched.accommodation_details
                                ?.accommodation_type &&
                              Boolean(
                                errors.accommodation_details?.accommodation_type
                              )
                            }
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&:hover fieldset": {
                                  borderColor: colors.secondary,
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: colors.primary,
                                },
                              },
                            }}
                          >
                            <InputLabel id="accommodation-type-label">
                              Accommodation Type
                            </InputLabel>
                            <Select
                              labelId="accommodation-type-label"
                              id="accommodation_details.accommodation_type"
                              name="accommodation_details.accommodation_type"
                              value={
                                values.accommodation_details.accommodation_type
                              }
                              onChange={handleChange}
                              onBlur={handleBlur}
                              label="Accommodation Type"
                            >
                              <MenuItem value="HOUSE">House</MenuItem>
                              <MenuItem value="APARTMENT">Apartment</MenuItem>
                            </Select>
                            {touched.accommodation_details
                              ?.accommodation_type &&
                              errors.accommodation_details
                                ?.accommodation_type && (
                                <FormHelperText>
                                  {
                                    errors.accommodation_details
                                      .accommodation_type
                                  }
                                </FormHelperText>
                              )}
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <FormControl
                            fullWidth
                            variant="outlined"
                            error={
                              touched.accommodation_details
                                ?.accommodation_ownership &&
                              Boolean(
                                errors.accommodation_details
                                  ?.accommodation_ownership
                              )
                            }
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&:hover fieldset": {
                                  borderColor: colors.secondary,
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: colors.primary,
                                },
                              },
                            }}
                          >
                            <InputLabel id="accommodation-ownership-label">
                              Ownership
                            </InputLabel>
                            <Select
                              labelId="accommodation-ownership-label"
                              id="accommodation_details.accommodation_ownership"
                              name="accommodation_details.accommodation_ownership"
                              value={
                                values.accommodation_details
                                  .accommodation_ownership
                              }
                              onChange={handleChange}
                              onBlur={handleBlur}
                              label="Ownership"
                            >
                              <MenuItem value="OWNED">Owned</MenuItem>
                              <MenuItem value="RENTED">Rented</MenuItem>
                            </Select>
                            {touched.accommodation_details
                              ?.accommodation_ownership &&
                              errors.accommodation_details
                                ?.accommodation_ownership && (
                                <FormHelperText>
                                  {
                                    errors.accommodation_details
                                      .accommodation_ownership
                                  }
                                </FormHelperText>
                              )}
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <FormControl
                            fullWidth
                            variant="outlined"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&:hover fieldset": {
                                  borderColor: colors.secondary,
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: colors.primary,
                                },
                              },
                            }}
                          >
                            <InputLabel id="accommodation-joint-independent-label">
                              Joint/Independent
                            </InputLabel>
                            <Select
                              labelId="accommodation-joint-independent-label"
                              id="accommodation_details.accommodation_ownership_joint_or_independent"
                              name="accommodation_details.accommodation_ownership_joint_or_independent"
                              value={
                                values.accommodation_details
                                  .accommodation_ownership_joint_or_independent
                              }
                              onChange={handleChange}
                              onBlur={handleBlur}
                              label="Joint/Independent"
                            >
                              <MenuItem value="JOINT">Joint</MenuItem>
                              <MenuItem value="INDEPENDENT">
                                Independent
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            id="accommodation_details.monthly_rent"
                            name="accommodation_details.monthly_rent"
                            label="Monthly Rent"
                            type="number"
                            value={values.accommodation_details.monthly_rent}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            variant="outlined"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  PKR
                                </InputAdornment>
                              ),
                              inputProps: { min: 0 },
                            }}
                            disabled={
                              values.accommodation_details
                                .accommodation_ownership !== "RENTED"
                            }
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&:hover fieldset": {
                                  borderColor: colors.secondary,
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: colors.primary,
                                },
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* Financial Information */}
                  <SectionTitle
                    icon={<FaMoneyBillWave />}
                    title="Financial Information"
                  />

                  {/* Family Liabilities */}
                  <Typography
                    variant="h6"
                    sx={{
                      mt: 2,
                      mb: 2,
                      fontWeight: 600,
                      color: colors.secondary,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <FaMoneyBillWave
                      style={{ marginRight: "8px", fontSize: "1rem" }}
                    />
                    Family Liabilities
                  </Typography>
                  <Card
                    variant="outlined"
                    sx={{
                      mb: 4,
                      borderRadius: "10px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      border: `1px solid ${colors.border}`,
                      overflow: "hidden",
                    }}
                  >
                    <CardContent>
                      <FieldArray name="family_liabilities">
                        {({ push, remove }) => (
                          <Box>
                            {values.family_liabilities.length > 0 &&
                              values.family_liabilities.map(
                                (liability, index) => (
                                  <Box
                                    key={index}
                                    sx={{
                                      mb: 3,
                                      p: 2,
                                      border: `1px solid ${colors.border}`,
                                      borderRadius: "8px",
                                      bgcolor:
                                        touched.family_liabilities?.[index] &&
                                        (errors.family_liabilities?.[
                                          index
                                        ] as any)
                                          ? "rgba(231, 76, 60, 0.05)"
                                          : "white",
                                      transition: "all 0.3s ease",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        mb: 2,
                                      }}
                                    >
                                      <Typography
                                        variant="subtitle1"
                                        sx={{
                                          color: colors.primary,
                                          fontWeight: 600,
                                        }}
                                      >
                                        Liability #{index + 1}
                                      </Typography>
                                      <IconButton
                                        onClick={() => remove(index)}
                                        color="error"
                                        size="small"
                                        sx={{
                                          color: colors.error,
                                          "&:hover": {
                                            bgcolor: "rgba(231, 76, 60, 0.1)",
                                          },
                                        }}
                                      >
                                        <FaTrash />
                                      </IconButton>
                                    </Box>

                                    <Grid container spacing={3}>
                                      <Grid item xs={12} sm={6}>
                                        <TextField
                                          fullWidth
                                          name={`family_liabilities.${index}.nature`}
                                          label="Nature of Liability"
                                          value={liability.nature}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          error={
                                            touched.family_liabilities?.[index]
                                              ?.nature &&
                                            Boolean(
                                              (
                                                errors.family_liabilities?.[
                                                  index
                                                ] as any
                                              )?.nature
                                            )
                                          }
                                          helperText={
                                            touched.family_liabilities?.[index]
                                              ?.nature &&
                                            (
                                              errors.family_liabilities?.[
                                                index
                                              ] as any
                                            )?.nature
                                          }
                                          variant="outlined"
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              "&:hover fieldset": {
                                                borderColor: colors.secondary,
                                              },
                                              "&.Mui-focused fieldset": {
                                                borderColor: colors.primary,
                                              },
                                            },
                                          }}
                                        />
                                      </Grid>

                                      <Grid item xs={12} sm={6}>
                                        <TextField
                                          fullWidth
                                          name={`family_liabilities.${index}.amount`}
                                          label="Outstanding Amount"
                                          type="number"
                                          value={liability.amount}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          error={
                                            touched.family_liabilities?.[index]
                                              ?.amount &&
                                            Boolean(
                                              (
                                                errors.family_liabilities?.[
                                                  index
                                                ] as any
                                              )?.amount
                                            )
                                          }
                                          helperText={
                                            touched.family_liabilities?.[index]
                                              ?.amount &&
                                            (
                                              errors.family_liabilities?.[
                                                index
                                              ] as any
                                            )?.amount
                                          }
                                          variant="outlined"
                                          InputProps={{
                                            startAdornment: (
                                              <InputAdornment position="start">
                                                PKR
                                              </InputAdornment>
                                            ),
                                            inputProps: {
                                              min: 0,
                                            },
                                          }}
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              "&:hover fieldset": {
                                                borderColor: colors.secondary,
                                              },
                                              "&.Mui-focused fieldset": {
                                                borderColor: colors.primary,
                                              },
                                            },
                                          }}
                                        />
                                      </Grid>

                                      <Grid item xs={12}>
                                        <TextField
                                          fullWidth
                                          name={`family_liabilities.${index}.description`}
                                          label="Description"
                                          value={liability.description}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          error={
                                            touched.family_liabilities?.[index]
                                              ?.description &&
                                            Boolean(
                                              (
                                                errors.family_liabilities?.[
                                                  index
                                                ] as any
                                              )?.description
                                            )
                                          }
                                          helperText={
                                            touched.family_liabilities?.[index]
                                              ?.description &&
                                            (
                                              errors.family_liabilities?.[
                                                index
                                              ] as any
                                            )?.description
                                          }
                                          variant="outlined"
                                          multiline
                                          rows={2}
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              "&:hover fieldset": {
                                                borderColor: colors.secondary,
                                              },
                                              "&.Mui-focused fieldset": {
                                                borderColor: colors.primary,
                                              },
                                            },
                                          }}
                                        />
                                      </Grid>
                                    </Grid>
                                  </Box>
                                )
                              )}

                            <Button
                              type="button"
                              variant="outlined"
                              startIcon={<FaPlus />}
                              onClick={() =>
                                push({
                                  amount: 0,
                                  nature: "",
                                  description: "",
                                })
                              }
                              sx={{
                                mt: 2,
                                borderColor: colors.secondary,
                                color: colors.secondary,
                                "&:hover": {
                                  borderColor: colors.primary,
                                  backgroundColor: "rgba(30, 77, 140, 0.04)",
                                },
                              }}
                            >
                              Add Liability
                            </Button>
                          </Box>
                        )}
                      </FieldArray>
                    </CardContent>
                  </Card>

                  {/* Family Expenses */}
                  <Typography
                    variant="h6"
                    sx={{
                      mt: 2,
                      mb: 2,
                      fontWeight: 600,
                      color: colors.secondary,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <FaMoneyBillWave
                      style={{ marginRight: "8px", fontSize: "1rem" }}
                    />
                    Family Expenses
                  </Typography>
                  <Card
                    variant="outlined"
                    sx={{
                      mb: 4,
                      borderRadius: "10px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      border: `1px solid ${colors.border}`,
                      overflow: "hidden",
                    }}
                  >
                    <CardContent>
                      <FieldArray name="family_expenses">
                        {({ push, remove }) => (
                          <Box>
                            {values.family_expenses.length > 0 &&
                              values.family_expenses.map((expense, index) => (
                                <Box
                                  key={index}
                                  sx={{
                                    mb: 3,
                                    p: 2,
                                    border: `1px solid ${colors.border}`,
                                    borderRadius: "8px",
                                    bgcolor:
                                      touched.family_expenses?.[index] &&
                                      (errors.family_expenses?.[index] as any)
                                        ? "rgba(231, 76, 60, 0.05)"
                                        : "white",
                                    transition: "all 0.3s ease",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      mb: 2,
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        color: colors.primary,
                                        fontWeight: 600,
                                      }}
                                    >
                                      Expense #{index + 1}
                                    </Typography>
                                    <IconButton
                                      onClick={() => remove(index)}
                                      color="error"
                                      size="small"
                                      sx={{
                                        color: colors.error,
                                        "&:hover": {
                                          bgcolor: "rgba(231, 76, 60, 0.1)",
                                        },
                                      }}
                                    >
                                      <FaTrash />
                                    </IconButton>
                                  </Box>

                                  <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                      <TextField
                                        fullWidth
                                        name={`family_expenses.${index}.nature`}
                                        label="Nature"
                                        value={expense.nature}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={
                                          touched.family_expenses?.[index]
                                            ?.nature &&
                                          Boolean(
                                            (
                                              errors.family_expenses?.[
                                                index
                                              ] as any
                                            )?.nature
                                          )
                                        }
                                        helperText={
                                          touched.family_expenses?.[index]
                                            ?.nature &&
                                          (
                                            errors.family_expenses?.[
                                              index
                                            ] as any
                                          )?.nature
                                        }
                                        variant="outlined"
                                        // sx={{
                                        //   "& .MuiOutlinedInput-root": {
                                        //     "&:hover fieldset": {
                                        //       borderColor: colors.secondary,
                                        //     },
                                        //     "&.Mui-focused fieldset": {
                                        //       borderColor: colors.primary,
                                        //     },
                                        //   }}
                                      />
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                      <TextField
                                        fullWidth
                                        name={`family_expenses.${index}.amount`}
                                        label="Amount"
                                        type="number"
                                        value={expense.amount}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={
                                          touched.family_expenses?.[index]
                                            ?.amount &&
                                          Boolean(
                                            (
                                              errors.family_expenses?.[
                                                index
                                              ] as any
                                            )?.amount
                                          )
                                        }
                                        helperText={
                                          touched.family_expenses?.[index]
                                            ?.amount &&
                                          (
                                            errors.family_expenses?.[
                                              index
                                            ] as any
                                          )?.amount
                                        }
                                        variant="outlined"
                                        InputProps={{
                                          startAdornment: (
                                            <InputAdornment position="start">
                                              PKR
                                            </InputAdornment>
                                          ),
                                          inputProps: { min: 0 },
                                        }}
                                        sx={{
                                          "& .MuiOutlinedInput-root": {
                                            "&:hover fieldset": {
                                              borderColor: colors.secondary,
                                            },
                                            "&.Mui-focused fieldset": {
                                              borderColor: colors.primary,
                                            },
                                          },
                                        }}
                                      />
                                    </Grid>

                                    <Grid item xs={12}>
                                      <TextField
                                        fullWidth
                                        name={`family_expenses.${index}.description`}
                                        label="Description"
                                        value={expense.description}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={
                                          touched.family_expenses?.[index]
                                            ?.description &&
                                          Boolean(
                                            (
                                              errors.family_expenses?.[
                                                index
                                              ] as any
                                            )?.description
                                          )
                                        }
                                        helperText={
                                          touched.family_expenses?.[index]
                                            ?.description &&
                                          (
                                            errors.family_expenses?.[
                                              index
                                            ] as any
                                          )?.description
                                        }
                                        variant="outlined"
                                        multiline
                                        rows={2}
                                        sx={{
                                          "& .MuiOutlinedInput-root": {
                                            "&:hover fieldset": {
                                              borderColor: colors.secondary,
                                            },
                                            "&.Mui-focused fieldset": {
                                              borderColor: colors.primary,
                                            },
                                          },
                                        }}
                                      />
                                    </Grid>
                                  </Grid>
                                </Box>
                              ))}

                            <Button
                              type="button"
                              variant="outlined"
                              startIcon={<FaPlus />}
                              onClick={() =>
                                push({
                                  amount: 0,
                                  nature: "",
                                  description: "",
                                })
                              }
                              sx={{
                                mt: 2,
                                borderColor: colors.secondary,
                                color: colors.secondary,
                                "&:hover": {
                                  borderColor: colors.primary,
                                  backgroundColor: "rgba(30, 77, 140, 0.04)",
                                },
                              }}
                            >
                              Add Expense
                            </Button>
                          </Box>
                        )}
                      </FieldArray>
                    </CardContent>
                  </Card>

                  {/* Personal Statements */}
                  <SectionTitle
                    icon={<FaInfoCircle />}
                    title="Personal Statements"
                  />
                  <Card
                    variant="outlined"
                    sx={{
                      mb: 4,
                      borderRadius: "10px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      border: `1px solid ${colors.border}`,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor: colors.primary,
                        py: 1,
                        px: 2,
                        borderTopLeftRadius: "10px",
                        borderTopRightRadius: "10px",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ color: "white", fontWeight: 500 }}
                      >
                        Share your personal statements
                      </Typography>
                    </Box>
                    <CardContent>
                      <FieldArray name="personal_statements">
                        {() => (
                          <Box>
                            {values.personal_statements.map(
                              (statement, index) => (
                                <Box
                                  key={index}
                                  sx={{
                                    mb: 3,
                                    p: 2,
                                    border: `1px solid ${colors.border}`,
                                    borderRadius: "8px",
                                    bgcolor:
                                      touched.personal_statements?.[index] &&
                                      (errors.personal_statements?.[
                                        index
                                      ] as any)
                                        ? "rgba(231, 76, 60, 0.05)"
                                        : "white",
                                    transition: "all 0.3s ease",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      mb: 2,
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        color: colors.primary,
                                        fontWeight: 600,
                                      }}
                                    >
                                      Statement #{index + 1}
                                    </Typography>

                                    {/* Trash icon kept for looks but disabled */}
                                    <IconButton
                                      disabled
                                      color="error"
                                      size="small"
                                      sx={{
                                        color: colors.error,
                                        opacity: 0.4,
                                        cursor: "not-allowed",
                                      }}
                                    >
                                      <FaTrash />
                                    </IconButton>
                                  </Box>

                                  <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                      <TextField
                                        fullWidth
                                        name={`personal_statements.${index}.question`}
                                        label="Question"
                                        value={statement.question}
                                        onBlur={handleBlur}
                                        variant="outlined"
                                        InputProps={{ readOnly: true }}
                                        sx={{
                                          "& .MuiOutlinedInput-root": {
                                            "&:hover fieldset": {
                                              borderColor: colors.secondary,
                                            },
                                            "&.Mui-focused fieldset": {
                                              borderColor: colors.primary,
                                            },
                                          },
                                        }}
                                      />
                                    </Grid>

                                    <Grid item xs={12}>
                                      <TextField
                                        fullWidth
                                        name={`personal_statements.${index}.response`}
                                        label="Response"
                                        value={statement.response}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        multiline
                                        rows={4}
                                        variant="outlined"
                                        error={
                                          touched.personal_statements?.[index]
                                            ?.response &&
                                          Boolean(
                                            (
                                              errors.personal_statements?.[
                                                index
                                              ] as any
                                            )?.response
                                          )
                                        }
                                        helperText={
                                          touched.personal_statements?.[index]
                                            ?.response &&
                                          (
                                            errors.personal_statements?.[
                                              index
                                            ] as any
                                          )?.response
                                        }
                                        sx={{
                                          "& .MuiOutlinedInput-root": {
                                            "&:hover fieldset": {
                                              borderColor: colors.secondary,
                                            },
                                            "&.Mui-focused fieldset": {
                                              borderColor: colors.primary,
                                            },
                                          },
                                        }}
                                      />
                                    </Grid>
                                  </Grid>
                                </Box>
                              )
                            )}
                          </Box>
                        )}
                      </FieldArray>
                    </CardContent>
                  </Card>

                  {/* Documents */}
                  <SectionTitle icon={<FaFileAlt />} title="Documents" />
                  <Card
                    variant="outlined"
                    sx={{
                      mb: 4,
                      borderRadius: "10px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      border: `1px solid ${colors.border}`,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor: colors.primary,
                        py: 1,
                        px: 2,
                        borderTopLeftRadius: "10px",
                        borderTopRightRadius: "10px",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ color: "white", fontWeight: 500 }}
                      >
                        Upload required documents
                      </Typography>
                    </Box>
                    <CardContent>
                      <FieldArray name="documents_attached">
                        {() => (
                          <Box>
                            {values.documents_attached.map(
                              (document, index) => (
                                <Box
                                  key={index}
                                  sx={{
                                    mb: 3,
                                    p: 2,
                                    border: `1px solid ${colors.border}`,
                                    borderRadius: "8px",
                                    bgcolor:
                                      touched.documents_attached?.[index] &&
                                      (errors.documents_attached?.[
                                        index
                                      ] as any)
                                        ? "rgba(231, 76, 60, 0.05)"
                                        : "white",
                                    transition: "all 0.3s ease",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      mb: 2,
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        color: colors.primary,
                                        fontWeight: 600,
                                      }}
                                    >
                                      Document #{index + 1}
                                    </Typography>
                                  </Box>

                                  <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                      <TextField
                                        fullWidth
                                        name={`documents_attached.${index}.title`}
                                        label="Document Title"
                                        value={document.title}
                                        onBlur={handleBlur}
                                        variant="outlined"
                                        InputProps={{ readOnly: true }}
                                        sx={{
                                          "& .MuiOutlinedInput-root": {
                                            "&:hover fieldset": {
                                              borderColor: colors.secondary,
                                            },
                                            "&.Mui-focused fieldset": {
                                              borderColor: colors.primary,
                                            },
                                          },
                                        }}
                                      />
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                      <FormControl
                                        fullWidth
                                        error={
                                          touched.documents_attached?.[index]
                                            ?.attachment_url &&
                                          Boolean(
                                            (
                                              errors.documents_attached?.[
                                                index
                                              ] as any
                                            )?.attachment_url
                                          )
                                        }
                                      >
                                        <InputLabel
                                          shrink
                                          htmlFor={`document-upload-${index}`}
                                        >
                                          Document File
                                        </InputLabel>
                                        <Box sx={{ mt: 2 }}>
                                          <input
                                            accept="application/pdf,image/*"
                                            id={`document-upload-${index}`}
                                            type="file"
                                            style={{ display: "none" }}
                                            onChange={(e) =>
                                              handleDocumentUpload(
                                                e,
                                                index,
                                                setFieldValue,
                                                values
                                              )
                                            }
                                          />
                                          <label
                                            htmlFor={`document-upload-${index}`}
                                          >
                                            <Button
                                              variant="outlined"
                                              component="span"
                                              startIcon={<FaUpload />}
                                              disabled={
                                                uploadingDocuments[index]
                                              }
                                              sx={{
                                                borderColor: colors.secondary,
                                                color: colors.secondary,
                                                "&:hover": {
                                                  borderColor: colors.primary,
                                                  backgroundColor:
                                                    "rgba(30, 77, 140, 0.04)",
                                                },
                                              }}
                                            >
                                              {uploadingDocuments[index]
                                                ? "Uploading..."
                                                : "Upload Document"}
                                            </Button>
                                          </label>

                                          {/* {document.attachment_url && (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            mt: 1,
                                          }}
                                        >
                                          <FaCheckCircle color={colors.success} style={{ marginRight: 8 }} />
                                          <MuiLink
                                            href={document.attachment_url}
                                            target="_blank"
                                            rel="noopener"
                                            variant="body2"
                                            underline="hover"
                                            sx={{ color: colors.primary }}
                                          >
                                            {document.attachment_url.split("/").pop()}
                                          </MuiLink>
                                        </Box>
                                      )} */}
                                          {document.attachment_url.length >
                                            0 && (
                                            <Box sx={{ mt: 1 }}>
                                              {document.attachment_url.map(
                                                (u, i) => (
                                                  <Box
                                                    key={i}
                                                    sx={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      mt: 0.5,
                                                    }}
                                                  >
                                                    <MuiLink
                                                      href={u}
                                                      target="_blank"
                                                      rel="noopener"
                                                      variant="body2"
                                                      underline="hover"
                                                      sx={{
                                                        color: colors.primary,
                                                        mr: 1,
                                                        flex: 1,
                                                        minWidth: 0, // Allow shrinking
                                                        overflow: "hidden",
                                                        textOverflow:
                                                          "ellipsis",
                                                        whiteSpace: "nowrap",
                                                      }}
                                                    >
                                                      {u.split("/").pop()}
                                                    </MuiLink>
                                                    <IconButton
                                                      size="small"
                                                      onClick={() => {
                                                        const docs = [
                                                          ...values.documents_attached,
                                                        ];
                                                        docs[index] = {
                                                          ...docs[index],
                                                          attachment_url: docs[
                                                            index
                                                          ].attachment_url.filter(
                                                            (_, idx) =>
                                                              idx !== i
                                                          ),
                                                        };
                                                        setFieldValue(
                                                          "documents_attached",
                                                          docs
                                                        );
                                                      }}
                                                      sx={{
                                                        color: colors.error,
                                                        flexShrink: 0, // Don't shrink the delete button
                                                        minWidth: "auto",
                                                      }}
                                                    >
                                                      <FaTrash />
                                                    </IconButton>
                                                  </Box>
                                                )
                                              )}
                                            </Box>
                                          )}
                                          {touched.documents_attached?.[index]
                                            ?.attachment_url &&
                                            (
                                              errors.documents_attached?.[
                                                index
                                              ] as any
                                            )?.attachment_url && (
                                              <FormHelperText error>
                                                {
                                                  (
                                                    errors.documents_attached?.[
                                                      index
                                                    ] as any
                                                  )?.attachment_url
                                                }
                                              </FormHelperText>
                                            )}
                                        </Box>
                                      </FormControl>
                                    </Grid>
                                  </Grid>
                                </Box>
                              )
                            )}
                          </Box>
                        )}
                      </FieldArray>
                    </CardContent>
                  </Card>

                  {/* Submit and Save Draft Buttons */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 2,
                      mt: 4,
                      mb: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    {/* Save Draft Button - Only show for new applications */}
                    {!isEditMode && (
                      <Button
                        type="button"
                        variant="outlined"
                        size="large"
                        disabled={isSavingDraft || loading}
                        startIcon={<FaSave />}
                        onClick={() => handleSaveDraft(values)}
                        sx={{
                          py: 1.5,
                          px: 5,
                          borderColor: colors.accent,
                          borderWidth: "2px",
                          color: colors.accent,
                          "&:hover": {
                            borderColor: colors.accent,
                            borderWidth: "2px",
                            bgcolor: "rgba(243, 156, 18, 0.08)",
                            transform: "translateY(-2px)",
                            boxShadow: "0 4px 12px rgba(243, 156, 18, 0.2)",
                          },
                          fontSize: "1rem",
                          fontWeight: 600,
                          transition: "all 0.3s ease",
                        }}
                      >
                        {isSavingDraft ? "Saving Draft..." : "Save Draft"}
                      </Button>
                    )}

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={loading || isSavingDraft}
                      startIcon={<FaCheckCircle />}
                      sx={{
                        py: 1.5,
                        px: 6,
                        bgcolor: colors.primary,
                        "&:hover": {
                          bgcolor: colors.secondary,
                        },
                        fontSize: "1.1rem",
                        fontWeight: 600,
                        color: "white",
                        boxShadow: "0 4px 10px rgba(30, 77, 140, 0.2)",
                        transition: "all 0.3s ease",
                      }}
                    >
                      {loading
                        ? "Submitting..."
                        : isEditMode
                        ? "Update Application"
                        : "Submit Application"}
                    </Button>
                  </Box>
                </Box>
              </Form>
            )}
          </Formik>
        )}
      </Paper>

      {/* Floating Save Draft Button - Only for new applications */}
      {!isEditMode && showFloatingSaveButton && formikRef.current && (
        <Box
          sx={{
            position: "fixed",
            bottom: 30,
            right: 30,
            zIndex: 1000,
            animation: "fadeInUp 0.3s ease",
            "@keyframes fadeInUp": {
              from: {
                opacity: 0,
                transform: "translateY(20px)",
              },
              to: {
                opacity: 1,
                transform: "translateY(0)",
              },
            },
          }}
        >
          <Button
            variant="contained"
            size="large"
            disabled={isSavingDraft || loading}
            startIcon={<FaSave />}
            onClick={() => handleSaveDraft(formikRef.current.values)}
            sx={{
              py: 1.5,
              px: 4,
              bgcolor: colors.accent,
              color: "white",
              fontWeight: 600,
              fontSize: "1rem",
              boxShadow: "0 6px 20px rgba(243, 156, 18, 0.4)",
              "&:hover": {
                bgcolor: "#e67e22",
                boxShadow: "0 8px 25px rgba(243, 156, 18, 0.5)",
                transform: "translateY(-2px)",
              },
              transition: "all 0.3s ease",
              borderRadius: "8px",
            }}
          >
            {isSavingDraft ? "Saving..." : "Save Draft"}
          </Button>
        </Box>
      )}
    </Container>
  );
}
